import React, { useState, useEffect, useMemo, useRef, memo } from "react";

type CoverSettings = {
  mode?: "forward" | string; // forward：洞透明，外側不透明；其他值視為反相
  type?: any;
  holePercent?: number;
  centerPct?: { xPct: number; yPct: number };
  shape?: "circle" | "strip" | "square";
  stripPivot?: "center" | "top" | "bottom";
  stripPinnedEdge?: "none" | "top" | "bottom";
  // 兼容舊參數
  stripAnchor?: "center" | "top" | "bottom";

  // 仲裁需要的中繼資料
  actorId?: string;
  sessionId?: string;
  seq?: number;
  phase?: "begin" | "move" | "end";
  ts?: number; // 以發送端 Date.now() 為主，沒有就用本地時間
  holePercentSetting?: boolean;
};

type Props = { settings?: CoverSettings };

const ACTIVE_HOLD_MS = 250; // 貼黏時間：避免兩位老師快速交錯造成閃爍
const EXPIRE_MS = 1500; // 會話視為離線的逾時（沒新包就可被接管）

const clamp = (v: number, min: number, max: number) =>
  Math.max(min, Math.min(max, v));

const isSafariLike = () =>
  /iPad/.test(navigator.userAgent) ||
  (navigator.userAgent.includes("Mac") && "ontouchend" in document);

const MaskUser: React.FC<Props> = ({ settings }) => {
  const containerRef = useRef<HTMLDivElement>(null);

  /** ========= 仲裁狀態 ========= */
  const [applied, setApplied] = useState<CoverSettings | null>(null);
  // const activeRef = useRef<{ sessionId: string; actorId?: string; lastTs: number } | null>(null);
  const activeRef = useRef<any>(null);
  const seqRef = useRef<number>(-1);

  // 把缺欄位的 settings 正規化
  const norm = (s?: CoverSettings): CoverSettings | null => {
    if (!s) return null;
    const ts = s.ts ?? Date.now();
    const sessionId =
      s.sessionId ?? (s.actorId ? `${s.actorId}-legacy` : "legacy");
    const shape = (s.shape as any) ?? "circle";
    const stripPinnedEdge: "none" | "top" | "bottom" =
      s.stripPinnedEdge ??
      (s.stripAnchor === "top"
        ? "top"
        : s.stripAnchor === "bottom"
        ? "bottom"
        : "none");
    const stripPivot: "center" | "top" | "bottom" =
      s.stripPivot ?? (s.stripAnchor ? (s.stripAnchor as any) : "center");
    const holePercent = Number(s.holePercent ?? 0);
    return {
      ...s,
      ts,
      sessionId,
      shape,
      stripPinnedEdge,
      stripPivot,
      holePercent,
    };
  };

  useEffect(() => {
    const inc = norm(settings);
    if (!inc) return;

    const now = inc.ts!;
    const active = activeRef.current;

    // 首包或沒有活躍會話 → 直接採用
    if (!active) {
      activeRef.current = {
        sessionId: inc.sessionId!,
        actorId: inc.actorId,
        lastTs: now,
      };
      seqRef.current = inc.seq ?? -1;
      setApplied(inc);
      return;
    }

    if (inc.sessionId === active.sessionId) {
      // 同一個會話：按序號接收，亂序丟掉
      if (
        inc.seq == null ||
        inc.seq >= seqRef.current ||
        inc.holePercentSetting
      ) {
        seqRef.current = inc.seq ?? seqRef.current + 1;
        activeRef.current = { ...activeRef.current, lastTs: now };
        setApplied(inc);
      }
      return;
    }

    // 另一個會話的包：判斷是否要「切換控制者」
    const age = now - active.lastTs;
    const wantTakeover = inc.phase === "begin";
    const sticky = age < ACTIVE_HOLD_MS; // 現任控制者剛更新過 → 先忽略以防閃爍

    if (wantTakeover) {
      // 切換控制者
      activeRef.current = {
        sessionId: inc.sessionId!,
        actorId: inc.actorId,
        lastTs: now,
      };
      seqRef.current = inc.seq ?? -1;
      setApplied(inc);
      return;
    }

    // 否則忽略這個外來會話的包（避免閃爍）
  }, [settings]);

  const s = useMemo<
    Required<
      Pick<
        CoverSettings,
        | "mode"
        | "holePercent"
        | "centerPct"
        | "shape"
        | "stripPinnedEdge"
        | "stripPivot"
      >
    >
  >(
    () => ({
      mode: (applied?.mode ?? "forward") as any,
      holePercent: applied?.holePercent ?? 0,
      centerPct: applied?.centerPct ?? { xPct: 50, yPct: 50 },
      shape: (applied?.shape ?? "circle") as any,
      stripPinnedEdge: (applied?.stripPinnedEdge ?? "none") as any,
      stripPivot: (applied?.stripPivot ?? "center") as any,
    }),
    [applied],
  );

  // ---- 共用工具 ----
  const getScale = (el: HTMLElement) => {
    const rect = el.getBoundingClientRect();
    const ow = el.offsetWidth || rect.width;
    const oh = el.offsetHeight || rect.height;
    return { rect, ow, oh };
  };

  const getCenterPx = () => {
    const el = containerRef.current;
    if (!el) return { cx: 0, cy: 0, w: 0, h: 0 };
    if (isSafariLike()) {
      const { ow, oh } = getScale(el);
      return {
        cx: (s.centerPct.xPct / 100) * ow,
        cy: (s.centerPct.yPct / 100) * oh,
        w: ow,
        h: oh,
      };
    } else {
      const w = el.clientWidth;
      const h = el.clientHeight;
      return {
        cx: (s.centerPct.xPct / 100) * w,
        cy: (s.centerPct.yPct / 100) * h,
        w,
        h,
      };
    }
  };

  const getRadiusPx = () => {
    const el = containerRef.current;
    if (!el) return 0;
    const w = isSafariLike() ? getScale(el).ow : el.clientWidth;
    return (s.holePercent / 100) * w * 0.5;
  };

  // strip：從 pivot/pinned 邏輯求帶狀 top/bottom
  const getStripBandPx = (w: number, h: number, py: number) => {
    const bandH = clamp((s.holePercent / 100) * h, 0, h);
    if (s.stripPinnedEdge === "top") return { top: 0, bottom: bandH };
    if (s.stripPinnedEdge === "bottom") return { top: h - bandH, bottom: h };

    if (s.stripPivot === "top") {
      const top = clamp(py, 0, h);
      const bottom = Math.min(top + bandH, h);
      return { top, bottom };
    }
    if (s.stripPivot === "bottom") {
      const bottom = clamp(py, bandH, h);
      return { top: bottom - bandH, bottom };
    }
    // center
    const topUnclamped = py - bandH / 2;
    const top = clamp(topUnclamped, 0, h - bandH);
    return { top, bottom: top + bandH };
  };

  // ---- 各形狀的樣式產生 ----
  const buildCircleMask = () => {
    const forward = s.mode === "forward";
    const r = getRadiusPx();
    if (r <= 0) {
      return {
        WebkitMaskImage: forward
          ? "none"
          : "linear-gradient(transparent, transparent)",
        maskImage: forward
          ? "none"
          : "linear-gradient(transparent, transparent)",
      } as const;
    }
    const { cx, cy } = getCenterPx();
    const stop1 = `${r}px`;
    const stop2 = `${r + 1}px`;
    const mask = `radial-gradient(circle at ${cx}px ${cy}px, ${
      forward ? "transparent" : "#222222"
    } ${stop1}, ${forward ? "#222222" : "transparent"} ${stop2})`;
    return {
      WebkitMaskImage: mask,
      maskImage: mask,
      WebkitMaskRepeat: "no-repeat" as const,
      maskRepeat: "no-repeat" as const,
    };
  };

  const buildStripMask = () => {
    const forward = s.mode === "forward";
    const el = containerRef.current;
    if (!el) return {};
    const w = isSafariLike() ? getScale(el).ow : el.clientWidth;
    const h = isSafariLike() ? getScale(el).oh : el.clientHeight;
    const py = (s.centerPct.yPct / 100) * h;
    const { top, bottom } = getStripBandPx(w, h, py);
    if (bottom - top <= 0) {
      return {
        WebkitMaskImage: forward
          ? "none"
          : "linear-gradient(transparent, transparent)",
        maskImage: forward
          ? "none"
          : "linear-gradient(transparent, transparent)",
      } as const;
    }
    const a = forward ? "#222222" : "transparent";
    const b = forward ? "transparent" : "#222222";
    const mask = `linear-gradient(
      to bottom,
      ${a} 0px,
      ${a} ${top}px,
      ${b} ${top}px,
      ${b} ${bottom}px,
      ${a} ${bottom}px,
      ${a} 100%
    )`;
    return {
      WebkitMaskImage: mask,
      maskImage: mask,
      WebkitMaskRepeat: "no-repeat" as const,
      maskRepeat: "no-repeat" as const,
    };
  };

  // 方形：使用獨立的 image/position/size 屬性（避免切換閃爍）
  const buildSquareMask = () => {
    const forward = s.mode === "forward";
    const el = containerRef.current;
    if (!el) return {};
    const w = isSafariLike() ? getScale(el).ow : el.clientWidth;
    const h = isSafariLike() ? getScale(el).oh : el.clientHeight;

    const side = (s.holePercent / 100) * w;
    if (side <= 0) {
      if (forward)
        return { WebkitMaskImage: "none", maskImage: "none" } as const;
      return {
        WebkitMaskImage: forward
          ? "none"
          : "linear-gradient(transparent, transparent)",
        maskImage: forward
          ? "none"
          : "linear-gradient(transparent, transparent)",
      } as const;
    }

    const cx = (s.centerPct.xPct / 100) * w;
    const cy = (s.centerPct.yPct / 100) * h;
    const half = side / 2;

    const left = clamp(cx - half, 0, w);
    const right = clamp(cx + half, left, w);
    const top = clamp(cy - half, 0, h);
    const bottom = clamp(cy + half, top, h);

    const wL = left;
    const wR = w - right;
    const hT = top;
    const hB = h - bottom;
    const cW = Math.max(0, right - left);
    const cH = Math.max(0, bottom - top);

    if (forward) {
      // 四個外框，盡量只畫有尺寸的層
      const imgs: string[] = [];
      const poss: string[] = [];
      const sizes: string[] = [];
      const reps: string[] = [];

      const push = (pos: string, size: string) => {
        imgs.push("linear-gradient(#000,#000)");
        poss.push(pos);
        sizes.push(size);
        reps.push("no-repeat");
      };

      if (hT > 0) push(`0px 0px`, `${w}px ${hT}px`); // 上
      if (hB > 0) push(`0px ${bottom}px`, `${w}px ${hB}px`); // 下
      if (wL > 0) push(`0px 0px`, `${wL}px ${h}px`); // 左
      if (wR > 0) push(`${right}px 0px`, `${wR}px ${h}px`); // 右

      if (imgs.length === 0) {
        return {
          opacity: 0,
        } as const;
      }

      return {
        WebkitMaskImage: imgs.join(", "),
        maskImage: imgs.join(", "),
        WebkitMaskPosition: poss.join(", "),
        maskPosition: poss.join(", "),
        WebkitMaskSize: sizes.join(", "),
        maskSize: sizes.join(", "),
        WebkitMaskRepeat: reps.join(", "),
        maskRepeat: reps.join(", "),
      } as const;
    } else {
      // 反相：只畫中間方塊
      if (cW <= 0 || cH <= 0) {
        return {
          WebkitMaskImage: "linear-gradient(transparent, transparent)",
          maskImage: "linear-gradient(transparent, transparent)",
        } as const;
      }
      return {
        WebkitMaskImage: "linear-gradient(#000,#000)",
        maskImage: "linear-gradient(#000,#000)",
        WebkitMaskPosition: `${left}px ${top}px`,
        maskPosition: `${left}px ${top}px`,
        WebkitMaskSize: `${cW}px ${cH}px`,
        maskSize: `${cW}px ${cH}px`,
        WebkitMaskRepeat: "no-repeat" as const,
        maskRepeat: "no-repeat" as const,
      } as const;
    }
  };

  const layerStyle =
    s.shape === "strip"
      ? buildStripMask()
      : s.shape === "square"
      ? buildSquareMask()
      : buildCircleMask();

  // 觀眾端不需要指標交互；為避免方形切換閃爍，square 關閉 transition
  const overlayWillChange =
    s.shape === "square"
      ? "mask-image, -webkit-mask-image, mask-position, -webkit-mask-position, mask-size, -webkit-mask-size"
      : undefined;

  return (
    <div
      ref={containerRef}
      style={{
        width: "100%",
        height: "100%",
        overflow: "hidden",
        position: "absolute",
        inset: 0,
        zIndex: 4,
        userSelect: "none",
      }}
    >
      <div
        style={{
          position: "absolute",
          inset: 0,
          background: "#222222",
          willChange: overlayWillChange,
          pointerEvents: "auto",
          ...layerStyle,
        }}
      />
    </div>
  );
};

export default memo(MaskUser);
